import { useCallback, useRef, useState } from 'react';
import { InView } from 'react-intersection-observer';

import { Fab, makeStyles } from '@material-ui/core';
import { duration } from '@material-ui/core/styles/transitions';

import { RiArrowUpLine as ArrowUpwardIcon } from '@remixicon/react';

import { FadeIn } from './FadeIn';

type Props = {
  label?: string;
};

const useStyles = makeStyles(theme => ({
  fab: {
    position: 'fixed',
    left: '50%',
    transform: 'translateX(-50%)',
    bottom: theme.spacing(2),
    zIndex: 4,
    backgroundColor: 'var(--bui-bg-surface-1)',
    color: 'var(--bui-fg-primary)',
    '&:hover, &:active, &:active:hover': {
      transform: 'translateX(-50%) scale(1.04)',
      backgroundColor: 'var(--bui-bg-tint-hover)',
    },
  },
  fabIcon: {
    marginRight: theme.spacing(1),
  },
}));

export const ScrollTopButton = ({ label = 'Back to top' }: Props) => {
  const classes = useStyles();
  const [showButton, setShowButton] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const scrollToTop = useCallback(() => {
    if (!ref?.current) return;
    try {
      const container = ref.current.parentNode as HTMLElement;
      container?.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      // Ignore scroll errors for browser compatibility
    }
  }, []);

  return (
    <>
      <div ref={ref} />
      <InView onChange={inView => setShowButton(!inView)} />
      {showButton && (
        <FadeIn timeout={duration.enteringScreen}>
          <Fab
            variant="extended"
            size="medium"
            className={classes.fab}
            onClick={scrollToTop}
          >
            <ArrowUpwardIcon className={classes.fabIcon} />
            {label}
          </Fab>
        </FadeIn>
      )}
    </>
  );
};
